import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { MoreHorizontal, FileSpreadsheet, Trash2 } from 'lucide-react';

/**
 * Compact "İşlemler" dropdown that consolidates the secondary toolbar
 * actions (Sunday column toggle, Excel import/export, Clear Week)
 * so the primary toolbar stays uncluttered.
 *
 * Sunday toggle does NOT close the menu — the operator usually
 * tweaks it. Excel and Clear Week DO close it (they open dialogs/confirms).
 *
 * Props:
 *   showSunday,   setShowSunday   : controlled toggle
 *   isSuperAdmin                  : gates Excel + Clear Week items
 *   onOpenImportExport            : callback when Excel item clicked
 *   onClearWeek                   : callback when Clear Week clicked
 *   t                              : i18n function (used for title only)
 */
export function PlanningActionsMenu({
  showSunday, setShowSunday,
  isSuperAdmin,
  canCopyWeek,
  onOpenImportExport,
  onClearWeek,
  t,
}) {
  const [open, setOpen] = useState(false);
  // Şub 2026 — "Haftayı Temizle" artık hafta kopyalama yetkisi olanlar için de görünür
  const canClearWeek = isSuperAdmin || canCopyWeek;
  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        className="h-8"
        onClick={() => setOpen((v) => !v)}
        data-testid="planning-actions-toggle"
      >
        <MoreHorizontal className="h-3.5 w-3.5 mr-1" /> İşlemler
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute top-full left-0 mt-1.5 z-40 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl py-1.5"
            data-testid="planning-actions-menu"
          >
            <ToggleRow
              testId="planning-toggle-sunday"
              checked={showSunday}
              onClick={() => setShowSunday((s) => !s)}
              label="Pazar sütununu göster"
            />
            {(isSuperAdmin || canClearWeek) && (
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            )}
            {isSuperAdmin && (
              <button
                type="button"
                onClick={() => { setOpen(false); onOpenImportExport(); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-700/40 flex items-center gap-2"
                data-testid="planning-import-export-open"
              >
                <FileSpreadsheet className="h-3.5 w-3.5 text-slate-500" /> Excel İçe / Dışa Aktar
              </button>
            )}
            {canClearWeek && (
              <button
                type="button"
                onClick={() => { setOpen(false); onClearWeek(); }}
                className="w-full text-left px-3 py-1.5 text-xs text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 flex items-center gap-2"
                data-testid="planning-clear-week"
                title={t('planning.deleteWeekTitle')}
              >
                <Trash2 className="h-3.5 w-3.5" /> Haftayı Temizle
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ToggleRow({ checked, onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-700/40 flex items-center gap-2"
      data-testid={testId}
    >
      <span className={`inline-flex h-4 w-4 items-center justify-center rounded border ${checked ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-slate-300 text-transparent'}`}>✓</span>
      {label}
    </button>
  );
}
